import { Signal } from 'typed-signals';
import type { Container } from 'pixi.js';
import cloneDeep from 'lodash/cloneDeep';
import { LevelTasksTracker } from './tasks/LevelTasksTracker';
import type { GameSceneOptions } from './scene/GameSceneOptions';
import { GameSceneView } from './views/GameSceneView';
import { GameAreaController } from './boardMVC/controllers/GameAreaController';
import { BoosterController } from './boardMVC/controllers/BoosterController';
import { BoosterPanelView } from './BoosterPanelView';
import { GAME_ECONOMY_SETTINGS, ReviveReason } from './GameConstants';
import type { BoosterType } from './boosters/types';
import type { TopBarView } from './views/TopBarView';
import { showPreLevelOverlays } from './behaviours/showPreLevelOverlays';
import { initTutorial } from './boardMVC/controllers/initTutorial';
import { EventScheduler } from '../utils/EventScheduler';
import { BoardEventType } from './boardMVC/models/board/BoardEvents';
import type { BoardModel } from './boardMVC/models/board/BoardModel';
import type { TrayModel } from './boardMVC/models/tray/TrayModel';
import { GameEndFlowController } from './GameEndFlowController';
import { startWinFlow } from '../behaviours/winFlow/startWinFlow';
import { FailFlowResolution, startFailFlow } from '../behaviours/failFlow/startFailFlow';
import { showSettingsMenu } from '../behaviours/showSettingsMenu';
import { ReviveAction } from './boardMVC/models/actions/ReviveAction';
import type { LevelData } from './level/LevelData';
import type { GameSceneMetrics } from './scene/GameSceneMetrics';
import { createGameSceneMetrics } from './scene/GameSceneMetrics';
import type { App } from '../app/App';
import { MultiplatformHandler } from '../app/multiplatform/MultiplatformHandler';
import { IAPPlacement } from '../app/iap/types';
import { AdErrorPopup } from './views/popups/AdErrorPopup';
import type { BlockerModel } from './boardMVC/types';
import { BlockerType } from './boardMVC/types';
import { soundManager, SoundTrackId } from '../app/SoundManager';
import type { Scene } from '../app/SceneManager';
import { openShopPopup } from '../behaviours/openShopPopup';
import { exitToMainMenu } from '../behaviours/exitToMainMenu';
import { restartLevel } from '../behaviours/restartLevel';
import type { ProgressSaveData } from '../app/state/AppStateController';
import type { DynamicDifficultyRefillMiddleware } from '../app/splitTest/tests/DynamicDifficultyRefillMiddleware';
import { openHowToPlayPopup } from '../behaviours/openHowToPlayPopup';
import type { GameTutorialController } from './tutorials/game/GameTutorialController';
import { openBoosterIntroductionPopup } from '../behaviours/openBoosterIntroductionPopup';
import { openBlockerIntroductionPopup } from '../behaviours/openBlockerIntroductionPopup';
import type { BlockerIntroType } from './blockers/constants';
import { connectTaskTrackerToBoard } from './tasks/connectTaskTrackerToBoard';
import type { TileModel } from './boardMVC/models/board/TileModel';

interface GameSceneControllerDependencies {
  app: App;
  root: Container;
}

export class GameSceneController {
  public static readonly onLevelLost = new Signal<(data: { levelIndex: number; attemptId: string }) => void>();

  public static readonly onLevelWon = new Signal<(data: { levelIndex: number; attemptId: string }) => void>();

  public static readonly onRevive = new Signal<(data: { levelIndex: number; attemptId: string }) => void>();

  public readonly onRevive = new Signal();

  public readonly onUndoStateCreated = new Signal<(progress: ProgressSaveData) => void>();

  public readonly onBoosterUsed = new Signal<(type: BoosterType) => void>();

  public readonly onExitToMainMenu = new Signal();

  private readonly levelTaskTracker: LevelTasksTracker;

  private readonly metrics: GameSceneMetrics;

  private readonly endFlowController: GameEndFlowController;

  private readonly boosterController: BoosterController;

  private readonly sceneView: GameSceneView;

  private readonly gameAreaController: GameAreaController;

  private readonly boosterPanelView: BoosterPanelView;

  private readonly scheduler = new EventScheduler();

  private inWinState = false;

  private paused = false;

  private readonly refillMiddleware: DynamicDifficultyRefillMiddleware | null = null;

  /** Timer for mid-level interstitial checks (in ms) */
  private midLevelAdCheckTimer = 0;

  /** How often to check for mid-level ads (in ms) - actual timing controlled by remote config */
  private static readonly midLevelAdCheckIntervalMs = 10_000;

  public tutorial: GameTutorialController | null = null;

  constructor(
    private readonly dependencies: GameSceneControllerDependencies,
    private readonly sceneOptions: GameSceneOptions,
    metrics?: GameSceneMetrics,
  ) {
    this.levelTaskTracker = this.initTaskTracker();
    const {
      dependencies: {
        app: { stateController, iap },
        app,
      },
    } = this;

    this.metrics = metrics ?? createGameSceneMetrics(stateController);

    this.sceneView = new GameSceneView({
      state: stateController,
      taskTracker: this.levelTaskTracker,
      iapAvailable: !!iap,
      versusMode: null,
    }).props({
      parent: this.dependencies.root,
    });

    this.sceneView.onHelpButtonClicked.connect(() => {
      openHowToPlayPopup(app);
    });

    // Initialize collection event tray if collection event is active
    const collectionEventTray = app.gameEvents.collectionEvent?.createInGameTrayView();
    if (collectionEventTray) {
      this.sceneView.addCollectionEventTray(collectionEventTray);
    }

    this.gameAreaController = this.initGameArea();
    this.endFlowController = this.initEndFlowController();
    this.initTopBar();
    const { view, controller } = this.initBoosters();
    this.boosterController = controller;
    this.boosterPanelView = view;
    connectTaskTrackerToBoard({
      animateBoardProgress: (tilesOrBlocker: BlockerModel | TileModel[]): void => {
        void this.sceneView.animateBoardProgress(tilesOrBlocker);
      },
      boardModel: this.getBoardModel(),
      boardView: this.sceneView.getBoardView() ?? null,
      levelTaskTracker: this.levelTaskTracker,
      trayModel: this.getTrayModel(),
    });
    this.hookUpBoard();
  }

  public getBoardModel(): BoardModel {
    return this.gameAreaController.boardModel;
  }

  public getTrayModel(): TrayModel {
    return this.gameAreaController.trayModel;
  }

  public getTasksTracker(): LevelTasksTracker {
    return this.levelTaskTracker;
  }

  public getSceneView(): GameSceneView {
    return this.sceneView;
  }

  public activate(): void {
    const {
      dependencies: {
        app: {
          stateController: state,
          tracking,
          controller: { popups, sceneManager },
        },
      },
      levelTaskTracker,
      sceneView,
      gameAreaController: { boardModel, trayModel },
      sceneOptions: { saveProgress, levelData },
    } = this;

    boardModel.onScoreUpdated(levelTaskTracker.getHexTally());
    trayModel.updateProgress(levelTaskTracker.getCombinedProgress());

    // UNDO blocker
    boardModel.beforeMakeMoveClean.connect(() => {
      this.onUndoStateCreated.emit(this.getSaveLevelData());
    });

    // this.initRefillMiddleware();

    this.hookUpSceneViewEvents();

    if (saveProgress && !state.isLevelStarted()) {
      tracking.trackLevelStarted(state.getLevelTrackingData());
      state.flagLevelStarted();
    }

    void (async (): Promise<void> => {
      if (this.endFlowController.checkLevelWon() || this.endFlowController.checkOutOfSpace()) return;

      if (levelData.tutorial) this.initTutorial();

      await showPreLevelOverlays({
        boosterPanel: this.boosterPanelView,
        popups,
        overlayContainer: sceneView.getOverlayContainer(),
        saveProgress,
        state,
        tasksTracker: levelTaskTracker,
      });

      this.tutorial?.start();

      trayModel.refillItems(false);
    })();

    MultiplatformHandler.gameStart();

    const onSceneActivated = (scene: Scene): void => {
      sceneManager.onSceneActivated.disconnect(onSceneActivated);
      // Keep the shared BGM playing when returning to the main menu; only stop it
      // when moving to a scene that manages its own audio (e.g. PVP, Editor).
      if (scene.id !== 'Game' && scene.id !== 'MainMenu') this.stopMusic();
    };
    sceneManager.onSceneActivated.connect(onSceneActivated);
    this.startMusic();
  }

  // private initRefillMiddleware(): void {
  //   const {
  //     gameAreaController: { trayModel },
  //     dependencies: {
  //       app: { macroBalancingController },
  //     },
  //   } = this;
  //
  //     trayModel.setRefillMiddleware(
  //       macroBalancingController.getMacroBalancingRefillMiddleware(() => this.levelTaskTracker.getCombinedProgress()),
  //     );
  //
  // }

  private initTutorial(): void {
    const {
      dependencies: { app },
      sceneOptions: { levelData },
    } = this;

    const tutorial = initTutorial(
      this.gameAreaController.view,
      this.getBoardModel(),
      levelData,
      this.getSceneView().getOverlayContainer(),
      this.sceneView.getTopBarView(),
      async (boosterType: BoosterType, collect: boolean) => {
        await openBoosterIntroductionPopup(app, boosterType, collect, this.boosterPanelView.getButton(boosterType));
      },
      (boosterType) => this.triggerBoosterUse(boosterType, true),
      async (blockerIntroType: BlockerIntroType) => {
        await openBlockerIntroductionPopup(app, blockerIntroType);
      },
    );

    tutorial.onFinished.connect(() => {
      // maybe check so booster panel just checks button availability every frame
      this.boosterPanelView.updateButtonStates();
    });
    this.gameAreaController.setTutorialController(tutorial);
    this.tutorial = tutorial;
  }

  private hookUpSceneViewEvents(): void {
    const { sceneView, gameAreaController } = this;

    sceneView.onScoreProgressDisplayed.connect((progress) => {
      gameAreaController.boardModel.onScoreUpdated(progress);
    });

    sceneView.onLastScoreProgressDisplayed.connect(() => {
      this.endFlowController.checkLevelWon();
    });
  }

  private initTaskTracker(): LevelTasksTracker {
    const {
      dependencies: {
        app: { stateController: state },
      },
      sceneOptions: { saveProgress, levelData },
    } = this;

    const taskTallies = saveProgress ? state.getLevelTaskTallies() : {};

    return new LevelTasksTracker(levelData, taskTallies);
  }

  private initGameArea(): GameAreaController {
    const gameAreaController = new GameAreaController({
      app: this.dependencies.app,
      levelData: this.sceneOptions.levelData,
      dragContainer: this.sceneView,
      isInBoosterMode: () => this.boosterController.hasActiveAction(),
      rewardedAdTrigger: async (): Promise<boolean> => {
        const response = await this.dependencies.app.ads?.showRewardedAd('locked-ad-space');

        if (response === 'Success') {
          return true;
        }

        if (response === 'Error') {
          await AdErrorPopup.show(this.dependencies.app.controller.popups);
        }

        return false;
      },
    });

    this.sceneView.addGameAreaViews(gameAreaController.view);

    return gameAreaController;
  }

  public debug = {
    triggerWinFlow: (): void => {
      this.levelTaskTracker.debugCompleteAllTasks();
      this.endFlowController.onLevelWon.emit();
    },
    triggerFailFlow: (): void => {
      this.endFlowController.onOutOfSpace.emit();
    },
  };

  private initEndFlowController(): GameEndFlowController {
    const {
      levelTaskTracker,
      dependencies: {
        app: { stateController: state },
      },
    } = this;

    const controller = new GameEndFlowController({
      boardModel: this.getBoardModel(),
      taskTracker: this.levelTaskTracker,
    });

    const topBarView = this.sceneView.getTopBarView();

    const levelIndex = state.getLevelIndex();
    const { levelAttemptId: attemptId } = state.getLevelTrackingData();

    controller.onLevelWon.connect(async () => {
      GameSceneController.onLevelWon.emit({ levelIndex, attemptId });

      this.inWinState = true;

      this.stopMusic();

      this.pauseGame();
      this.gameAreaController.destroy();

      await this.cancelBoosterUse();
      await this.scheduler.wait(200, false);

      this.boosterPanelView.interactiveChildren = false;
      topBarView.setCoinCounterParent(topBarView.pixi.stage);
      this.sceneView.fadeBoardArea(0);

      void startWinFlow(this.dependencies.app, {
        options: this.sceneOptions,
        taskTracker: this.levelTaskTracker,
        sceneView: this.sceneView,
        metaProperties: this.refillMiddleware?.getMetaProps() ?? [],
      });
    });

    controller.onOutOfSpace.connect(async () => {
      const {
        dependencies: {
          app,
          app: { stateController },
        },
      } = this;
      this.pauseGame();
      await this.cancelBoosterUse();
      this.sceneView.fadeBoardArea(0);
      await this.scheduler.wait(200, false);

      this.stopMusic();

      this.sceneView.getTopBarView().setFailFlowState(true);

      const failFlowResolution = await startFailFlow(this.dependencies.app, {
        tasks: this.levelTaskTracker.tasks,
        metaProperties: this.refillMiddleware?.getMetaProps() ?? [],
      });

      switch (failFlowResolution) {
        case FailFlowResolution.ReviveBySpecialOffer:
        case FailFlowResolution.ReviveByCoins:
        case FailFlowResolution.ReviveByAd:
          GameSceneController.onRevive.emit({ levelIndex, attemptId });
          this.sceneView.getTopBarView().setFailFlowState(false);
          this.sceneView.fadeBoardArea(1);
          void this.performRevive(failFlowResolution);
          this.startMusic();
          break;
        case FailFlowResolution.RestartLevel:
          GameSceneController.onLevelLost.emit({ levelIndex, attemptId });
          // this.dependencies.app.platform.eventTracker?.trackEvent('game_over');
          this.dependencies.app.platform.logEvent('game_over');
          MultiplatformHandler.gameOver(state.getLevelIndex(), levelTaskTracker.getCombinedTally());
          restartLevel(app, this.sceneOptions);
          break;
        case FailFlowResolution.ExitToMenu:
          GameSceneController.onLevelLost.emit({ levelIndex, attemptId });
          // this.dependencies.app.platform.eventTracker?.trackEvent('game_over');
          this.dependencies.app.platform.logEvent('game_over');
          stateController.resetLevel();
          exitToMainMenu(app);
          break;
        default:
          break;
      }
    });

    return controller;
  }

  private async performRevive(failFlowResolution: FailFlowResolution): Promise<void> {
    let reason: ReviveReason | null = null;

    switch (failFlowResolution) {
      case FailFlowResolution.ReviveByAd:
        reason = ReviveReason.Ad;
        break;
      case FailFlowResolution.ReviveByCoins:
        reason = ReviveReason.Coins;
        break;
      case FailFlowResolution.ReviveBySpecialOffer:
        reason = ReviveReason.SpecialOffer;
        break;
      default:
        break;
    }

    if (reason === null) return;

    this.sceneView.fadeBoardArea(1);
    MultiplatformHandler.gameStart();
    this.resumeGame();
    void new ReviveAction(this.getTrayModel(), this.getBoardModel()).execute(
      GAME_ECONOMY_SETTINGS.reviveSpacesCleared[reason],
    );
    this.metrics.revivesUsed += 1;
    this.onRevive.emit();
  }

  private initBoosters(): { controller: BoosterController; view: BoosterPanelView } {
    const {
      dependencies: {
        app,
        app: { stateController: state, resources, iap },
      },
      gameAreaController: { boardModel, trayModel },
    } = this;

    const economyMap = GAME_ECONOMY_SETTINGS.boosters;

    const boosterController = new BoosterController({
      controller: this.gameAreaController,
      gameSceneController: this,
      resources,
      state,
      iap,
    });

    boosterController.onBoosterUsed.connect((type) => {
      this.onBoosterUsed.emit(type);
    });

    const boosterPanelView = new BoosterPanelView(
      state,
      economyMap,
      (boosterType: BoosterType) => boosterController.canAffordBoosterAction(boosterType),
      (boosterType: BoosterType) => {
        if (this.tutorial?.isBoosterButtonLocked(boosterType)) return false;
        return boosterController.canExecuteBoosterAction(boosterType);
      },
      () => iap != null,
    );

    boosterPanelView.hookupBoard(boardModel, trayModel);

    boosterPanelView.onBoosterClicked.connect(async (boosterType: BoosterType) => {
      if (this.tutorial && !this.tutorial.canUseBooster(boosterType)) return;

      if (state.getBoosterInventoryAmount(boosterType) === 0) {
        if (!resources.canAfford(GAME_ECONOMY_SETTINGS.boosters[boosterType].coinCost)) {
          void openShopPopup(app, IAPPlacement.Level, true);
          return;
        }
        resources.buyBooster(boosterType);
      }

      void this.triggerBoosterUse(boosterType);
    });

    this.sceneView.addBoosterPanel(boosterPanelView);

    return { view: boosterPanelView, controller: boosterController };
  }

  private async triggerBoosterUse(boosterType: BoosterType, hideCancel = false): Promise<void> {
    const { boosterController, sceneView, boosterPanelView } = this;

    const requiresOverlay = !boosterController.isInstantAction(boosterType);
    if (requiresOverlay)
      await sceneView.showBoosterUserOverlay(
        boosterType,
        hideCancel
          ? null
          : (): void => {
              void this.cancelBoosterUse();
            },
      );

    await boosterController.executeBoosterAction(boosterType);

    // TODO HS-307 check if that's really necessary
    boosterPanelView.updateButtonStates();
    if (requiresOverlay) await sceneView.hideBoosterUserOverlay();
  }

  private async cancelBoosterUse(): Promise<void> {
    if (!this.boosterController.hasActiveAction()) return;
    this.boosterController.cancelActiveAction();
    await this.sceneView.hideBoosterUserOverlay();
  }

  private initTopBar(): TopBarView {
    const {
      app,
      app: { iap },
    } = this.dependencies;

    const topBarView = this.sceneView.getTopBarView();

    topBarView.onSettingsButtonClicked.connect(() => {
      showSettingsMenu(this.dependencies.app, {
        retryFlowConfig: {
          sceneOptions: this.sceneOptions,
          tasks: this.levelTaskTracker.tasks,
          metaProperties: this.refillMiddleware?.getMetaProps() ?? [],
        },
      });
    });

    topBarView.onMoneyCounterClicked.connect(async () => {
      if (!iap) return;
      if (app.controller.popups.stack.length > 0) return;

      void openShopPopup(app, IAPPlacement.Level);
    });

    return topBarView;
  }

  private hookUpBoard(): void {
    const {
      app: { tracking, stateController },
    } = this.dependencies;

    const boardModel = this.getBoardModel();

    boardModel.onStateUpdate.connect(() => {
      if (this.paused) return;
      this.saveLevelData();
      this.endFlowController.checkOutOfSpace();
    });

    boardModel.events.addListener(BoardEventType.BlockerRemoved, ({ blocker }) => {
      if (blocker.getType() === BlockerType.Ad) {
        tracking.trackRvTileUnlock({
          levelAttemptId: stateController.getLevelTrackingData().levelAttemptId,
          trigger: 'ad',
        });
      }
    });
  }

  private startMusic(): void {
    soundManager.playLoop(SoundTrackId.Music, 'Gameplay_Music', 100);
  }

  private stopMusic(): void {
    soundManager.fadeOutSound(SoundTrackId.Music, 'Gameplay_Music', 100, true);
  }

  public pauseGame(): void {
    this.paused = true;
    this.scheduler.paused = true;
    this.gameAreaController.getBoardView().pauseTweens(true);
  }

  public resumeGame(): void {
    this.paused = false;
    this.scheduler.paused = false;
    this.gameAreaController.getBoardView().resumeTweens(true);
  }

  public update(deltaTime: number): void {
    this.scheduler.update(deltaTime);
    this.sceneView.update(deltaTime);
    if (this.paused) return;
    this.gameAreaController.update(deltaTime);

    // Check for mid-level interstitials periodically
    this.updateMidLevelAdCheck(deltaTime);
  }

  /**
   * Periodically check if mid-level interstitial should be shown
   */
  private updateMidLevelAdCheck(deltaTime: number): void {
    // Don't check if in win state or if ads not available
    if (this.inWinState) return;

    const { ads } = this.dependencies.app;
    if (!ads) return;

    this.midLevelAdCheckTimer += deltaTime;

    if (this.midLevelAdCheckTimer < GameSceneController.midLevelAdCheckIntervalMs) return;
    this.midLevelAdCheckTimer = 0;

    // Check and show mid-level ad (this respects all cooldowns and configs)
    void this.tryShowMidLevelAd();
  }

  /**
   * Try to show mid-level interstitial ad
   */
  private async tryShowMidLevelAd(): Promise<void> {
    const { ads } = this.dependencies.app;
    if (!ads) return;

    // AdsPlayer handles all the config checks (enabled, cooldown, payer, etc.)
    if (!ads.shouldShowMidLevelInterstitial()) return;

    // Pause game while showing ad
    this.pauseGame();

    try {
      await ads.showMidLevelInterstitialAd();
    } finally {
      // Resume game after ad (whether shown or not)
      this.resumeGame();
    }
  }

  public exportLevelData(): LevelData {
    const {
      gameAreaController: { boardModel, trayModel },
    } = this;

    return {
      ...this.sceneOptions.levelData,
      board: boardModel.exportState(),
      tray: trayModel.exportState(),
      tutorial: { steps: [] },
    };
  }

  private getSaveLevelData(): ProgressSaveData {
    const { board, tray } = this.exportLevelData();

    return {
      levelData: { board, tray, tutorial: { steps: [] } },
      levelTaskTallies: cloneDeep(this.levelTaskTracker.getTaskTallies()),
      levelReviveUsed: this.metrics.revivesUsed,
    };
  }

  private saveLevelData(): void {
    if (this.inWinState) return;

    const {
      sceneOptions: { saveProgress },
      dependencies: {
        app: { stateController: state },
      },
    } = this;

    const saveData = this.getSaveLevelData();

    if (!saveProgress) return;

    state.updateCurrentLevelProgress(saveData);
  }

  public destroy(): void {
    this.sceneView.destroy();
    this.gameAreaController.destroy();
  }
}
